import {
  usePayrollRecordsQuery,
  useTaxYearReportQuery,
} from '@transactions/graphql';
import {fromSeconds} from 'common/date';
import * as React from 'react';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {cn} from 'src/lib/utils';
import {useModals} from 'src/Modals';

/** Record amounts are integer cents. */
const cents = (value: number): string =>
  (value / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });

/**
 * Imported paychecks, newest first, sectioned by calendar year with the
 * year's totals in the header. Employer chips appear only when the corpus
 * spans more than one employer. Rows open the read-only detail modal.
 */
export const PayrollView = () => {
  useBreadcrumb({label: 'Payroll', reset: true});
  const {data, loading, error} = usePayrollRecordsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const {data: taxData} = useTaxYearReportQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Taxes by tax year: withholding attributes by pay date (a tax year by
  // construction) across ALL records — the employer filter deliberately
  // does not apply, a tax year spans employers. Cash events come from the
  // report, attributed by the bound tax year field.
  const taxYears = React.useMemo(() => {
    const byYear = new Map<
      string,
      {withheld: number; paid: number; refunded: number}
    >();
    const at = (year: string) => {
      const existing = byYear.get(year) ?? {withheld: 0, paid: 0, refunded: 0};
      byYear.set(year, existing);
      return existing;
    };
    for (const record of data?.payrollRecords ?? []) {
      at(String(fromSeconds(record.payDate).year)).withheld +=
        record.taxesWithheld;
    }
    for (const row of taxData?.taxYearReport.rows ?? []) {
      const group = at(row.year);
      group.paid += row.paid;
      group.refunded += row.refunded;
    }
    return [...byYear.entries()].sort(([a], [b]) => b.localeCompare(a));
  }, [data, taxData]);
  const {payrollRecordModal} = useModals();
  const [employerFilter, setEmployerFilter] = React.useState<string | null>(
    null,
  );

  const records = React.useMemo(
    () =>
      (data?.payrollRecords ?? []).filter(
        (record) => employerFilter == null || record.employer === employerFilter,
      ),
    [data, employerFilter],
  );
  const employers = React.useMemo(
    () =>
      [...new Set((data?.payrollRecords ?? []).map((r) => r.employer))].sort(),
    [data],
  );

  const years = React.useMemo(() => {
    const byYear = new Map<number, typeof records>();
    for (const record of records) {
      const year = fromSeconds(record.payDate).year;
      const existing = byYear.get(year);
      if (existing) {
        existing.push(record);
      } else {
        byYear.set(year, [record]);
      }
    }
    // Records arrive newest-first; keep that order for the sections too.
    return [...byYear.entries()];
  }, [records]);

  if (error) {
    return (
      <div className="text-destructive p-8 text-center text-sm">
        Couldn't load payroll: {error.message}
      </div>
    );
  }
  if (loading && data == null) {
    return (
      <div className="text-muted-foreground p-8 text-center text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-foreground text-xl font-semibold">Payroll</h1>
            <p className="text-muted-foreground text-sm">
              Imported paychecks — gross pay, withholding, and what was
              deposited. Click a row for the full slip.
            </p>
          </div>
          {employers.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {[null, ...employers].map((employer) => (
                <button
                  key={employer ?? 'all'}
                  type="button"
                  onClick={() => setEmployerFilter(employer)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    employer === employerFilter
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {employer ?? 'All'}
                </button>
              ))}
            </div>
          )}
        </div>

        {(taxData?.taxYearReport.fieldBound === true ||
          (taxYears.length > 0 && taxData != null)) && (
          <section>
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="text-foreground text-base font-semibold">
                Taxes by tax year
              </h2>
              <span className="text-muted-foreground text-xs">
                withholding by pay date · payments and refunds by the Tax
                year field
              </span>
            </div>
            {taxData?.taxYearReport.fieldBound !== true && (
              <p className="text-muted-foreground text-sm">
                Bind a Tax year field on Defaults to attribute settlements
                and refunds to the year they settle — withholding alone is
                shown below.
              </p>
            )}
            {taxData?.taxYearReport.fieldBound === true &&
              taxData.taxYearReport.unattributedCount > 0 && (
                <p className="text-destructive mb-2 text-sm">
                  {taxData.taxYearReport.unattributedCount} payment
                  {taxData.taxYearReport.unattributedCount === 1 ? '' : 's'} in
                  the taxes category {taxData.taxYearReport.unattributedCount === 1 ? 'has' : 'have'} no
                  tax year yet — set the Tax year field on them so they count.
                </p>
              )}
            {taxYears.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs">
                    <th className="py-1 font-medium">Tax year</th>
                    <th className="py-1 text-right font-medium">Withheld</th>
                    <th className="py-1 text-right font-medium">Payments</th>
                    <th className="py-1 text-right font-medium">Refunds</th>
                    <th className="py-1 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {taxYears.map(([year, group]) => (
                    <tr key={year} className="border-border border-t">
                      <td className="text-foreground py-1.5 font-medium">
                        {year}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {cents(group.withheld)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {cents(group.paid)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {group.refunded > 0 ? `−${cents(group.refunded)}` : cents(0)}
                      </td>
                      <td className="py-1.5 text-right font-medium tabular-nums">
                        {cents(group.withheld + group.paid - group.refunded)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        {years.length === 0 && (
          <div className="text-muted-foreground py-12 text-center text-sm">
            No payroll imported yet. Run{' '}
            <code className="bg-muted rounded px-1 py-0.5">
              yarn workspace server import:payslips
            </code>{' '}
            to load parsed payslips.
          </div>
        )}

        {years.map(([year, yearRecords]) => {
          const totals = yearRecords.reduce(
            (sum, record) => ({
              gross: sum.gross + record.gross,
              taxes: sum.taxes + record.taxesWithheld,
              net: sum.net + record.net,
            }),
            {gross: 0, taxes: 0, net: 0},
          );
          return (
            <section key={year}>
              <div className="text-muted-foreground flex flex-wrap items-baseline justify-between gap-x-4 border-b pb-1 text-sm">
                <span className="text-foreground text-base font-semibold">
                  {year}
                </span>
                <span className="tabular-nums">
                  {yearRecords.length} paychecks · gross {cents(totals.gross)} ·
                  taxes {cents(totals.taxes)} · net {cents(totals.net)}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs">
                    <th className="py-1 font-normal">Pay date</th>
                    <th className="py-1 font-normal">Employer</th>
                    <th className="py-1 text-right font-normal">Gross</th>
                    <th className="py-1 text-right font-normal">Taxes</th>
                    <th className="py-1 text-right font-normal">Pre-tax</th>
                    <th className="py-1 text-right font-normal">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {yearRecords.map((record) => (
                    <tr
                      key={record._id}
                      className="border-border hover:bg-muted/50 cursor-pointer border-t"
                      onClick={() => payrollRecordModal.openModal({record})}
                    >
                      <td className="py-1.5 pr-2">
                        {fromSeconds(record.payDate).toLocaleString({
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td className="text-muted-foreground py-1.5 pr-2">
                        {record.employer}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {cents(record.gross)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {cents(record.taxesWithheld)}
                      </td>
                      <td className="text-muted-foreground py-1.5 text-right tabular-nums">
                        {cents(record.otherPretax)}
                      </td>
                      <td className="py-1.5 text-right font-medium tabular-nums">
                        {cents(record.net)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>
    </div>
  );
};
